import React, { lazy, Suspense, useEffect, useState } from 'react';

import { Divider, Input, LoadingButton, styled } from '@asicsdigital/oneasics-blocks';
import { yupResolver } from '@hookform/resolvers/yup';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useTracking } from 'react-tracking';
import * as yup from 'yup';

import { EMAIL_REGEX } from '../../components/form/shared';
import PageWrapper from '../../components/page-wrapper/PageWrapper';
import Spinner from '../../components/spinner/Spinner';
import { useFocusFormField } from '../../hooks/use-focus-form-field';
import { usersApi } from '../../services/api';
import { setOneTimeUseQueryParams } from '../../store/queryParamsSlice';
import { uiSlice } from '../../store/uiSlice';
import { resetWithoutAgeGate } from '../../store/userSlice';
import { GOOGLE_ID } from '../../utilities/constants';

const SocialButtonGroup = lazy(() => import('../../components/social-button-group/SocialButtonGroup'));

// When a user loads Lander, prefetch and cache the Register and Login views.
// Prefetch downloads/caches the bundle at "Low" priority, making sure not to block any DOM rendering.
import(/* webpackPrefetch: true */ '../register/Register');
import(/* webpackPrefetch: true */ '../login/Login');

const FormWrapper = styled.div({
  alignContent: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginTop: '8px',
  textAlign: 'center',
  '& button': { marginTop: '24px' },
});

const DisclaimerBottomWrapper = styled.span({
  marginTop: '40px',
  textAlign: 'center',
});

const Lander = () => {
  const {
    client: { clientId },
    ui: { emailFromLander },
    queryParams: { prefillEmail },
  } = useSelector((state) => state);

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { Track, trackEvent } = useTracking(
    { page: 'lander', event_type: 'pageview' },
    { dispatchOnMount: true },
  );

  const history = useHistory();

  const checkIfEmailExistsAndRedirect = (emailAddress) => {
    setIsLoading(true);
    dispatch(usersApi.endpoints.emailExists.initiate(encodeURIComponent(emailAddress))).then(
      response => {
        // When the status !== fulfilled, it means the user doesn't exist. This is a valid response, even though the
        // backend returns the 'user_not_found' error.
        if (response.status !== 'fulfilled') {
          const errorMessage = response?.error?.data?.reasons[0];
          if (errorMessage !== 'user_not_found') {
            // We have an actual error from the backend. Return false to indicate a validation error, showing the
            // "somethingWentWrong" validation message.
            setIsLoading(false);
            trackEvent({ event_type: 'error', error: errorMessage });
          }
          // The entered email does not have an account. Go to the register page.
          dispatch(uiSlice.actions.setEmailFromLander(emailAddress));
          dispatch(resetWithoutAgeGate());
          history.push({
            pathname: '/register',
          });
        } else {
          // The entered email has an account. Go to the login page.
          dispatch(uiSlice.actions.setEmailFromLander(emailAddress));
          history.push({
            pathname: '/login',
          });
        }
        // Reset the prefillEmail param
        dispatch(setOneTimeUseQueryParams({ prefillEmail: '' }));
      },
    );
  };

  // Yup validations are normally run all at once. This method allows us to run them sequentially so that we can
  // check for the existence of an account only if the email format is valid.
  yup.addMethod(yup.string, 'sequence', function runValidations(funcList) {
    return this.test(async (value, context) => { //eslint-disable-line
      try {
        for (const func of funcList) { //eslint-disable-line
          await func().validate(value); //eslint-disable-line
        }
      } catch ({ message }) {
        return context.createError({ message });
      }
      return true;
    });
  });

  // Check if the email format is valid. If so, check if it's associated with an account via API call.
  const schema = yup.object().shape({
    email: yup.string().sequence([
      () => yup.string().required(t('errors:missingRequiredField')).matches(EMAIL_REGEX, t('errors:invalidEmail')),
      () => yup.string().test('email-exists', t('errors:somethingWentWrong'), async (emailAddress) => {
        setIsLoading(true);
        const response = await dispatch(usersApi.endpoints.emailExists.initiate(encodeURIComponent(emailAddress)));

        // When the status !== fulfilled, it means the user doesn't exist. This is a valid response, even though the
        // backend returns the 'user_not_found' error.
        if (response.status !== 'fulfilled') {
          const errorMessage = response?.error?.data?.reasons[0];
          if (errorMessage !== 'user_not_found') {
            // We have an actual error from the backend. Return false to indicate a validation error, showing the
            // "somethingWentWrong" validation message.
            setIsLoading(false);
            trackEvent({ event_type: 'error', error: errorMessage });
            return false;
          }

          // The entered email does not have an account. Go to the register page.
          dispatch(uiSlice.actions.setEmailFromLander(emailAddress));
          dispatch(resetWithoutAgeGate());
          history.push({
            pathname: '/register',
          });

          return true;
        }

        // The entered email has an account. Go to the login page.
        dispatch(uiSlice.actions.setEmailFromLander(emailAddress));
        history.push({
          pathname: '/login',
        });

        return true;
      }),
    ]),
  });

  const { clearErrors, control, formState: { errors }, handleSubmit, register, reset, setFocus } = useForm({
    defaultValues: { email: emailFromLander || prefillEmail },
    resolver: yupResolver(schema),
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    reset({ email: emailFromLander || prefillEmail });
    if (prefillEmail && EMAIL_REGEX.test(prefillEmail)) {
      checkIfEmailExistsAndRedirect(prefillEmail);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillEmail]);

  const formCopy = t('formCopy', { returnObjects: true });

  useFocusFormField(setFocus, 'email');

  return (
    <Track>
      <PageWrapper
        subtitleText={t('lander.subtitle')}
        titleText={t('lander.title')}
      >
        <FormWrapper>
          {/* We're not actually submitting this form, but we need the validation from useForm() */}
          <form noValidate onSubmit={handleSubmit(() => {})}>
            <Input
              visible
              aria-describedby="error-email-required"
              aria-invalid={errors?.email ? 'true' : 'false'}
              clearErrors={clearErrors}
              control={control}
              disabled={false}
              errorMessage={errors?.email?.message}
              id="email"
              label={formCopy?.email?.label}
              name="email"
              register={register}
              type="email"
            />

            <LoadingButton id="continue" isLoading={isLoading} name="lander-continue" spinner={Spinner}>
              {t('lander.continue')}
            </LoadingButton>
          </form>
        </FormWrapper>

        <Divider text={
          clientId === 'runkeeper' || clientId?.startsWith('asics_mobile')
          ? t('lander.or')
          : t('lander.continueWith')}
        />

        <Suspense fallback={<div />}>
          <GoogleOAuthProvider clientId={GOOGLE_ID}>
            <SocialButtonGroup />
          </GoogleOAuthProvider>
        </Suspense>

        {t('lander.disclaimer_bottom') && <DisclaimerBottomWrapper>{t('lander.disclaimer_bottom')}</DisclaimerBottomWrapper>
        }
      </PageWrapper>
    </Track>
  );
};

Lander.propTypes = {};

export default Lander;
